/**
 * Runtime application configuration.
 *
 * Resolves each value from `window.__APP_CONFIG__` first — injected at runtime by
 * the App Service via the `/config.js` script the host server emits from its
 * environment (App Service Application Settings) — and falls back to the Vite
 * build-time `import.meta.env.VITE_*` value when no runtime config is present
 * (local `vite dev`, or the Node/SSG prerender pass where `window` is absent).
 *
 * This lets a SINGLE build serve every environment: dev and prod each read their
 * own API URL / environment from their App Service settings rather than a value
 * baked at build time. Build-time baking previously shipped DEV config to PROD —
 * the prod portals called the dev API and CORS-failed every request (Bug #7872).
 */
export interface AppConfig {
  apiBaseUrl: string;
  azureTenantId: string;
  azureClientId: string;
  apiClientId: string;
  environment: string;
  appInsightsConnectionString: string;
  appVersion: string;
}

declare global {
  interface Window {
    __APP_CONFIG__?: Partial<AppConfig>;
  }
}

const runtime: Partial<AppConfig> =
  typeof window !== 'undefined' && window.__APP_CONFIG__ ? window.__APP_CONFIG__ : {};

// `||` (not `??`) so an empty-string runtime value still falls through to the
// build-time fallback. /config.js emits only non-empty keys, but be defensive.
export const config: AppConfig = {
  apiBaseUrl: runtime.apiBaseUrl || import.meta.env.VITE_API_BASE_URL || '',
  azureTenantId: runtime.azureTenantId || import.meta.env.VITE_AZURE_TENANT_ID || '',
  azureClientId: runtime.azureClientId || import.meta.env.VITE_AZURE_CLIENT_ID || '',
  apiClientId: runtime.apiClientId || import.meta.env.VITE_API_CLIENT_ID || '',
  environment: runtime.environment || import.meta.env.VITE_APP_ENVIRONMENT || 'Development',
  appInsightsConnectionString:
    runtime.appInsightsConnectionString ||
    import.meta.env.VITE_APPINSIGHTS_CONNECTION_STRING ||
    '',
  appVersion: runtime.appVersion || import.meta.env.VITE_APP_VERSION || 'dev',
};
